/**
 * Group chat unread state (namat#36 P2).
 *
 * SHARED FILE: keep this file byte-identical in namat (web) and namat-mobile
 * (src/lib/groups/groupUnread.ts in both). Pure, no Firebase imports, so both
 * repos test the same rules.
 *
 * Read state lives on the server, one doc per member and group:
 *   groupReads/{userId}_{groupId} = { userId, groupId, lastReadAt: serverTimestamp() }
 * written when the member opens or reads that group's chat. A group's unread
 * count is the number of messages newer than lastReadAt that the member did
 * not send.
 *
 * A member with no read doc yet (first sight of the group, or of this
 * feature) starts as "read up to now": the caller writes the doc with
 * serverTimestamp() and shows 0, so only later messages count — no "99+" flood
 * on every busy group the first time badges appear.
 */

export const GROUP_READS_COLLECTION = 'groupReads';

/** Largest count a badge shows as a number; anything above is "99+". */
export const UNREAD_CAP = 99;

/**
 * How many messages newer than lastReadAt an unread listener fetches. Above
 * UNREAD_CAP so a "99+" survives a few of those messages being the reader's own.
 */
export const UNREAD_FETCH_LIMIT = 150;

export function groupReadDocId(userId: string, groupId: string): string {
  return `${userId}_${groupId}`;
}

/**
 * Milliseconds from a Firestore Timestamp, Date, epoch number or a plain
 * {seconds, nanoseconds} map. null when absent or unreadable.
 */
export function toMillis(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === 'object') {
    const v = value as { toMillis?: unknown; seconds?: unknown; nanoseconds?: unknown };
    if (typeof v.toMillis === 'function') {
      const ms = (v.toMillis as () => number)();
      return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
    }
    if (typeof v.seconds === 'number') {
      const nanos = typeof v.nanoseconds === 'number' ? v.nanoseconds : 0;
      return v.seconds * 1000 + Math.floor(nanos / 1e6);
    }
  }
  return null;
}

export interface UnreadMessageLike {
  userId?: string;
  createdAt?: unknown;
  /** Soft delete: { deleted: true, text: '', ... } keeps createdAt. */
  deleted?: boolean;
}

/**
 * Messages from others newer than lastReadAtMs (null = never read: all of
 * them). A soft-deleted message (deleted === true) never counts: it keeps
 * its createdAt, so without this a message deleted before it was read would
 * stay in the badge. Capped at UNREAD_CAP + 1 so callers can show "99+".
 */
export function countUnread(
  messages: readonly UnreadMessageLike[],
  myUserId: string,
  lastReadAtMs: number | null,
): number {
  let count = 0;
  for (const msg of messages) {
    if (msg.userId === myUserId) continue;
    if (msg.deleted === true) continue;
    const at = toMillis(msg.createdAt);
    if (lastReadAtMs !== null && (at === null || at <= lastReadAtMs)) continue;
    count++;
    if (count > UNREAD_CAP) break;
  }
  return count;
}

/**
 * Sum of the counts for groupIds (all keys when omitted). Groups outside the
 * list, e.g. another organization's, never reach the total. Capped like
 * countUnread.
 */
export function totalUnread(
  counts: Readonly<Record<string, number>>,
  groupIds?: Iterable<string>,
): number {
  const ids = groupIds ? Array.from(new Set(groupIds)) : Object.keys(counts);
  let total = 0;
  for (const id of ids) {
    const n = counts[id];
    if (typeof n === 'number' && n > 0) total += n;
    if (total > UNREAD_CAP) return UNREAD_CAP + 1;
  }
  return total;
}

/** Badge text: '' for none, the number up to UNREAD_CAP, then "99+". */
export function formatUnreadBadge(count: number): string {
  if (!(count > 0)) return '';
  return count > UNREAD_CAP ? `${UNREAD_CAP}+` : String(Math.floor(count));
}

/**
 * lastReadAt to count from. The server doc wins; without one, a legacy
 * on-device value (mobile before namat#36 P2) is used so a member's read
 * state survives the move; with neither, the group counts as read up to
 * nowMs (the caller then writes the server doc as the baseline).
 */
export function resolveLastReadMs(
  serverMs: number | null | undefined,
  deviceMs: number | null | undefined,
  nowMs: number,
): number {
  if (typeof serverMs === 'number' && Number.isFinite(serverMs)) return serverMs;
  if (typeof deviceMs === 'number' && Number.isFinite(deviceMs) && deviceMs > 0) return deviceMs;
  return nowMs;
}
